// =============================================================================
// Customer Modal - Nuovo/Modifica Cliente
// =============================================================================

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2, User, Mail, Phone, MapPin, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useCreateCustomer, useUpdateCustomer } from '../../api/hooks';
import { Customer } from '../../types';
import { cn } from '../../lib/utils';

// ===========================================================================
// Schema Validation
// ===========================================================================

const customerSchema = z.object({
  firstName: z.string().min(2, i18n.t('modal.firstNameMinChars', { ns: 'customers' })),
  lastName: z.string().min(2, i18n.t('modal.lastNameMinChars', { ns: 'customers' })),
  email: z.string().email(i18n.t('modal.invalidEmail', { ns: 'customers' })).optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
  city: z.string().optional().or(z.literal('')),
  province: z.string().optional().or(z.literal('')),
  postalCode: z.string().optional().or(z.literal('')),
  internalNotes: z.string().optional().or(z.literal('')),
});

type CustomerFormData = z.infer<typeof customerSchema>;

// ===========================================================================
// Props
// ===========================================================================

interface CustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null; // null = create mode
}

// ===========================================================================
// Component
// ===========================================================================

export function CustomerModal({ isOpen, onClose, customer }: CustomerModalProps) {
  const { t } = useTranslation('customers');
  const isEditMode = !!customer;

  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      province: '',
      postalCode: '',
      internalNotes: '',
    },
  });

  // Reset form when modal opens/closes or customer changes
  useEffect(() => {
    if (isOpen) {
      if (customer) {
        reset({
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email ?? '',
          phone: customer.phone ?? '',
          address: customer.address ?? '',
          city: customer.city ?? '',
          province: customer.province ?? '',
          postalCode: customer.postalCode ?? '',
          internalNotes: customer.internalNotes ?? '',
        });
      } else {
        reset({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          province: '',
          postalCode: '',
          internalNotes: '',
        });
      }
    }
  }, [isOpen, customer, reset]);

  const onSubmit = async (data: CustomerFormData) => {
    try {
      // Clean up empty strings to undefined
      const cleanData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || undefined,
        phone: data.phone || undefined,
        address: data.address || undefined,
        city: data.city || undefined,
        province: data.province || undefined,
        postalCode: data.postalCode || undefined,
        internalNotes: data.internalNotes || undefined,
      };

      if (isEditMode && customer) {
        await updateCustomer.mutateAsync({ id: customer.id, data: cleanData });
      } else {
        await createCustomer.mutateAsync(cleanData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving customer:', error);
      alert(isEditMode ? t('modal.updateError') : t('modal.createError'));
    }
  };

  if (!isOpen) return null;

  const isPending = createCustomer.isPending || updateCustomer.isPending || isSubmitting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditMode ? t('modal.editTitle') : t('modal.createTitle')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Personal Info Section */}
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <User className="w-4 h-4" />
                {t('modal.personalData')}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('modal.firstNameRequired')}
                  </label>
                  <input
                    type="text"
                    {...register('firstName')}
                    className={cn(
                      'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500',
                      errors.firstName ? 'border-red-300' : 'border-gray-300'
                    )}
                    placeholder={t('modal.firstNamePlaceholder')}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-xs text-red-500">{errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('modal.lastNameRequired')}
                  </label>
                  <input
                    type="text"
                    {...register('lastName')}
                    className={cn(
                      'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500',
                      errors.lastName ? 'border-red-300' : 'border-gray-300'
                    )}
                    placeholder={t('modal.lastNamePlaceholder')}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-xs text-red-500">{errors.lastName.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Info Section */}
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <Mail className="w-4 h-4" />
                {t('modal.contacts')}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common:fields.email')}
                  </label>
                  <input
                    type="email"
                    {...register('email')}
                    className={cn(
                      'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500',
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    )}
                    placeholder={t('modal.emailPlaceholder')}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    {t('common:fields.phone')}
                  </label>
                  <input
                    type="tel"
                    {...register('phone')}
                    className="w-full px-3 py-2 input"
                    placeholder={t('modal.phonePlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <MapPin className="w-4 h-4" />
                {t('modal.addressSection')}
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common:fields.address')}
                  </label>
                  <input
                    type="text"
                    {...register('address')}
                    className="w-full px-3 py-2 input"
                    placeholder={t('modal.addressPlaceholder')}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('common:fields.postalCode')}
                    </label>
                    <input
                      type="text"
                      {...register('postalCode')}
                      className="w-full px-3 py-2 input"
                      placeholder={t('modal.postalCodePlaceholder')}
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('common:fields.city')}
                    </label>
                    <input
                      type="text"
                      {...register('city')}
                      className="w-full px-3 py-2 input"
                      placeholder={t('modal.cityPlaceholder')}
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('common:fields.province')}
                    </label>
                    <input
                      type="text"
                      {...register('province')}
                      className="w-full px-3 py-2 input"
                      placeholder={t('modal.provincePlaceholder')}
                      maxLength={2}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <FileText className="w-4 h-4" />
                {t('modal.internalNotes')}
              </div>
              <textarea
                {...register('internalNotes')}
                rows={3}
                className="w-full px-3 py-2 input resize-none"
                placeholder={t('modal.notesPlaceholder')}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary px-4 py-2"
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex items-center gap-2 px-4 py-2 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEditMode ? t('common:buttons.saveChanges') : t('modal.createButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CustomerModal;
